import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Button, 
  Space, 
  message, 
  Modal, 
  Form, 
  Input, 
  InputNumber,
  Row,
  Col,
  Image,
  Tag,
  Empty,
  Spin,
  Select,
  Badge,
  Divider,
  Result,
  Alert,
  Collapse,
  Tooltip
} from 'antd';
import { 
  ShoppingCartOutlined, 
  PhoneOutlined, 
  MailOutlined,
  SearchOutlined,
  FilterOutlined,
  ClearOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  StarOutlined,
  UploadOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import { Upload } from 'antd';
import { env } from '../config/environment';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { customerApi } from '../services/customerApi';
import { useNavigate } from 'react-router-dom';
import QuantityBreakDisplay from '../components/QuantityBreakDisplay';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

interface QuantityBreak {
  minQuantity: number;
  maxQuantity: number | null;
  discountPercent: number | null;
  discountAmount: number | null;
  multiplier: number | null;
  isCustomerSpecific: boolean;
}

interface PricingTier {
  quantity: string; // "1-4", "5-9", "10+"
  unitPrice: number;
  customerPrice: number;
  volumePrice: number;
  savings?: number;
  savingsPercent?: number;
}

interface PriceBookItem {
  id: string;
  name: string;
  description?: string;
  sku?: string;
  unitPrice: number;
  retailPrice?: number; // Original price before customer discount
  hasDiscount?: boolean; // Whether customer discount is applied
  discountPercent?: number; // Discount percentage (e.g., 20 for 20% off)
  category?: string;
  itemType?: string;
  images?: string[];
  attributes?: Record<string, string>;
  isActive: boolean;
  eligibleForCustomerDiscount?: boolean;
  inStock?: boolean;
  isSpecialOrder?: boolean;
  stockStatus?: 'in_stock' | 'special_order' | 'out_of_stock';
  quantityBreaks?: QuantityBreak[];
  pricingTiers?: PricingTier[];
}

interface CartItem {
  item: PriceBookItem;
  quantity: number;
}

interface AttributeFilter {
  key: string;
  value: string;
}

/**
 * ProductSearch Page - Customer Portal
 * Allows customers to search and browse available products/services
 */
const ProductSearch: React.FC = () => {
  const navigate = useNavigate();
  const { customer } = useCustomerAuth();
  
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isQuoteModalVisible, setIsQuoteModalVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quoteResult, setQuoteResult] = useState<{
    success: boolean;
    estimate?: {
      id: string;
      estimateNumber: string;
      title: string;
      total: number;
    };
    emailSent?: boolean;
    nextSteps?: string;
  } | null>(null);
  const [quoteForm] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [specialOrderForm] = Form.useForm();
  
  // Search state
  const [items, setItems] = useState<PriceBookItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [attributeFilters, setAttributeFilters] = useState<AttributeFilter[]>([]);
  const [category, setCategory] = useState<string | undefined>();
  const [itemType, setItemType] = useState<string | undefined>();
  const [availableKeys, setAvailableKeys] = useState<string[]>([]);
  const [cartQuantities, setCartQuantities] = useState<Record<string, number>>({});

  // Customer pricing state
  const [customerPricingPercent, setCustomerPricingPercent] = useState<number | null>(null);

  // Special Order state
  const [specialOrderCategories, setSpecialOrderCategories] = useState<Array<{ id: string; category: string; description: string }>>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [submittingSpecialOrder, setSubmittingSpecialOrder] = useState(false);
  const [specialOrderImageUrl, setSpecialOrderImageUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [specialOrderResult, setSpecialOrderResult] = useState<{
    success: boolean;
    estimate?: {
      id: string;
      estimateNumber: string;
      title: string;
    };
    nextSteps?: string;
  } | null>(null);
  const [isSpecialOrderModalVisible, setIsSpecialOrderModalVisible] = useState(false);

  // Fetch available attribute keys on mount
  useEffect(() => {
    fetchAvailableKeys();
    // Load initial items
    searchItems();
    // Load special order categories
    fetchSpecialOrderCategories();
  }, []);

  const fetchSpecialOrderCategories = async () => {
    try {
      setLoadingCategories(true);
      const result = await customerApi.getSpecialOrderCategories();
      if (result.success) {
        setSpecialOrderCategories(result.categories || []);
      }
    } catch (error) {
      console.error('Error fetching special order categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Pre-fill form with customer data when modal opens
  useEffect(() => {
    if (isQuoteModalVisible && customer) {
      quoteForm.setFieldsValue({
        name: `${customer.firstName || ''} ${customer.lastName || ''}`.trim(),
        email: customer.email || '',
        phone: customer.phone || ''
      });
    }
  }, [isQuoteModalVisible, customer, quoteForm]);

  const fetchAvailableKeys = async () => {
    try {
      const token = localStorage.getItem('customerToken');
      const response = await fetch(`${env.apiBaseUrl}/item-attributes/customer/available-attributes`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableKeys(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching attribute keys:', error);
    }
  };

  const searchItems = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('customerToken');
      
      // Build query parameters
      const params = new URLSearchParams();
      
      // Add attribute filters
      if (attributeFilters.length > 0) {
        const attributeQuery = attributeFilters.reduce((acc, filter) => {
          if (filter.key && filter.value) {
            acc[filter.key] = filter.value;
          }
          return acc;
        }, {} as Record<string, string>);
        
        if (Object.keys(attributeQuery).length > 0) {
          params.append('attributes', JSON.stringify(attributeQuery));
        }
      }
      
      params.append('matchAll', 'true');
      if (category) params.append('category', category);
      if (itemType) params.append('itemType', itemType);
      params.append('isActive', 'true');

      const response = await fetch(`${env.apiBaseUrl}/item-attributes/customer/price-book?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setItems(data.data || []);
        // Capture customer pricing percent for display
        if (data.customerPricingPercent && data.customerPricingPercent !== 100) {
          setCustomerPricingPercent(data.customerPricingPercent);
        }
      } else {
        message.error('Failed to load products');
      }
    } catch (error) {
      console.error('Error searching items:', error);
      message.error('Failed to search products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFilter = () => {
    setAttributeFilters([...attributeFilters, { key: '', value: '' }]);
  };

  const handleRemoveFilter = (index: number) => {
    setAttributeFilters(attributeFilters.filter((_, i) => i !== index));
  };

  const handleFilterChange = (index: number, field: 'key' | 'value', value: string) => {
    const newFilters = [...attributeFilters];
    newFilters[index][field] = value;
    setAttributeFilters(newFilters);
  };

  const handleClearFilters = () => {
    setAttributeFilters([]);
    setCategory(undefined);
    setItemType(undefined);
    searchForm.resetFields();
  };

  // Add item to cart
  const handleAddToCart = (item: PriceBookItem, quantity: number) => {
    setCart(prevCart => {
      const existingIndex = prevCart.findIndex(cartItem => cartItem.item.id === item.id);
      
      if (existingIndex >= 0) {
        const newCart = [...prevCart];
        newCart[existingIndex].quantity += quantity;
        message.success(`Updated ${item.name} quantity in cart`);
        return newCart;
      } else {
        message.success(`Added ${item.name} to cart`);
        return [...prevCart, { item, quantity }];
      }
    });
    
    // Reset quantity input
    setCartQuantities(prev => ({ ...prev, [item.id]: 1 }));
  };

  const updateCartQuantity = (itemId: string, quantity: number) => {
    setCartQuantities(prev => ({ ...prev, [itemId]: quantity }));
  };

  const getItemImage = (item: PriceBookItem): string => {
    if (item.images && Array.isArray(item.images) && item.images.length > 0) {
      return item.images[0];
    }
    return 'https://via.placeholder.com/300x200?text=No+Image';
  };

  // Calculate cart total
  const cartTotal = cart.reduce((total, cartItem) => {
    return total + (Number(cartItem.item.unitPrice) * cartItem.quantity);
  }, 0);

  // Request quote - creates actual estimate and sends email
  const handleRequestQuote = async (values: any) => {
    setIsSubmitting(true);
    try {
      // Prepare cart items for the API
      const requestItems = cart.map(cartItem => ({
        id: cartItem.item.id,
        priceBookItemId: cartItem.item.id,
        name: cartItem.item.name,
        description: cartItem.item.description || '',
        quantity: cartItem.quantity,
        unitPrice: Number(cartItem.item.unitPrice),
        itemType: cartItem.item.itemType || 'service',
        isTaxable: true
      }));

      // Submit quote request to API
      const result = await customerApi.submitQuoteRequest({
        items: requestItems,
        serviceAddress: values.address || undefined,
        notes: values.message || undefined
      });

      console.log('Quote request result:', result);

      // Show success result
      setQuoteResult({
        success: true,
        estimate: result.estimate,
        emailSent: result.emailSent,
        nextSteps: result.nextSteps
      });

      // Clear cart after successful submission
      setCart([]);
      quoteForm.resetFields();

    } catch (error: any) {
      console.error('Quote request error:', error);
      message.error(error.response?.data?.message || 'Failed to send quote request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle viewing the created estimate
  const handleViewEstimate = () => {
    if (quoteResult?.estimate?.id) {
      navigate(`/estimates/${quoteResult.estimate.id}`);
    }
    setQuoteResult(null);
    setIsQuoteModalVisible(false);
  };

  // Close success modal and reset
  const handleCloseSuccess = () => {
    setQuoteResult(null);
    setIsQuoteModalVisible(false);
  };

  // Special Order handlers
  const handleSpecialOrderImageUpload = async (file: File) => {
    try {
      setUploadingImage(true);
      const result = await customerApi.uploadSpecialOrderImage(file);
      if (result.success) {
        setSpecialOrderImageUrl(result.url);
        message.success('Image uploaded successfully');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      message.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
    return false; // Prevent default upload behavior
  };

  const handleSubmitSpecialOrder = async (values: any) => {
    try {
      setSubmittingSpecialOrder(true);

      const result = await customerApi.submitSpecialOrder({
        category: values.category,
        itemType: values.itemType,
        description: values.description,
        imageUrl: specialOrderImageUrl || undefined
      });

      if (result.success) {
        setSpecialOrderResult({
          success: true,
          estimate: result.estimate,
          nextSteps: result.nextSteps
        });
        setIsSpecialOrderModalVisible(true);
        specialOrderForm.resetFields();
        setSpecialOrderImageUrl(null);
      }
    } catch (error: any) {
      console.error('Error submitting special order:', error);
      message.error(error.response?.data?.message || 'Failed to submit special order');
    } finally {
      setSubmittingSpecialOrder(false);
    }
  };

  const handleViewSpecialOrderEstimate = () => {
    if (specialOrderResult?.estimate?.id) {
      navigate(`/estimates/${specialOrderResult.estimate.id}`);
    }
    setSpecialOrderResult(null);
    setIsSpecialOrderModalVisible(false);
  };

  const handleCloseSpecialOrderSuccess = () => {
    setSpecialOrderResult(null);
    setIsSpecialOrderModalVisible(false);
  };

  const uploadProps: UploadProps = {
    name: 'file',
    accept: 'image/*',
    showUploadList: false,
    beforeUpload: (file) => {
      handleSpecialOrderImageUpload(file);
      return false;
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Browse Products & Services</Title>
        <Paragraph type="secondary">
          Search our catalog by attributes like color, size, brand, and more. Add items to your cart and request a quote.
        </Paragraph>
      </div>

      {/* Customer Discount Banner */}
      {customerPricingPercent && customerPricingPercent < 100 && (
        <Alert
          message={
            <Space>
              <span style={{ fontWeight: 'bold' }}>
                Your Pricing: {100 - customerPricingPercent}% off retail on eligible items!
              </span>
            </Space>
          }
          description="Discounted prices are shown in green with the retail price crossed out."
          type="success"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Cart Summary (Sticky) */}
      {cart.length > 0 && (
        <Card
          style={{
            position: 'sticky',
            top: 20,
            zIndex: 100,
            marginBottom: 24,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
          size="small"
        >
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <ShoppingCartOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                <Text strong>Cart ({cart.length} items)</Text>
              </Space>
              <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
                ${cartTotal.toFixed(2)}
              </Text>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                type="primary"
                icon={<MailOutlined />}
                onClick={() => setIsQuoteModalVisible(true)}
                block
              >
                Request Quote
              </Button>
              <Button
                onClick={() => setCart([])}
                danger
              >
                Clear Cart
              </Button>
            </div>
            {/* Cart Items Preview */}
            <div style={{ maxHeight: 150, overflowY: 'auto', marginTop: 8 }}>
              {cart.map((cartItem, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '4px 0',
                    borderBottom: index < cart.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}
                >
                  <Text ellipsis style={{ flex: 1 }}>
                    {cartItem.quantity}x {cartItem.item.name}
                  </Text>
                  <Text type="secondary">
                    ${(Number(cartItem.item.unitPrice) * cartItem.quantity).toFixed(2)}
                  </Text>
                </div>
              ))}
            </div>
          </Space>
        </Card>
      )}

      {/* Search Filters */}
      <Card title={<Space><FilterOutlined /> Search Filters</Space>} style={{ marginBottom: 24 }}>
        <Form form={searchForm} layout="vertical">
          {/* Attribute Filters */}
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {attributeFilters.map((filter, index) => (
              <Row key={index} gutter={8} align="middle">
                <Col span={10}>
                  <Select
                    placeholder="Select attribute"
                    value={filter.key}
                    onChange={(value) => handleFilterChange(index, 'key', value)}
                    style={{ width: '100%' }}
                    showSearch
                  >
                    {availableKeys.map(key => (
                      <Option key={key} value={key}>{key}</Option>
                    ))}
                  </Select>
                </Col>
                <Col span={10}>
                  <Input
                    placeholder="Enter value"
                    value={filter.value}
                    onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                  />
                </Col>
                <Col span={4}>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveFilter(index)}
                  />
                </Col>
              </Row>
            ))}
            
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddFilter}
              block
            >
              Add Attribute Filter
            </Button>
          </Space>

          <Divider />

          {/* Category and Type Filters */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Category">
                <Input
                  placeholder="Filter by category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Item Type">
                <Select
                  placeholder="Select item type"
                  value={itemType}
                  onChange={setItemType}
                  allowClear
                >
                  <Option value="service">Service</Option>
                  <Option value="material">Material</Option>
                  <Option value="labor">Labor</Option>
                  <Option value="equipment">Equipment</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Search Buttons */}
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={searchItems}
              loading={loading}
            >
              Search
            </Button>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          </Space>
        </Form>
      </Card>

      {/* Results */}
      <Card title={`Products (${items.length} found)`}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" tip="Loading products..." />
          </div>
        ) : items.length === 0 ? (
          <Empty description="No products found. Try adjusting your filters." />
        ) : (
          <Row gutter={[16, 16]}>
            {items.map(item => (
              <Col key={item.id} xs={24} sm={12} md={8} lg={6}>
                <Card
                  hoverable
                  style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                  cover={
                    <Image
                      alt={item.name}
                      src={getItemImage(item)}
                      height={200}
                      style={{ objectFit: 'cover' }}
                      fallback="https://via.placeholder.com/300x200?text=No+Image"
                    />
                  }
                  actions={[
                    <Space key="add" direction="vertical" size="small" style={{ width: '100%', padding: '0 8px' }}>
                      <InputNumber
                        min={1}
                        value={cartQuantities[item.id] || 1}
                        onChange={(value) => updateCartQuantity(item.id, value || 1)}
                        style={{ width: '100%' }}
                        addonBefore="Qty"
                      />
                      <Button
                        type="primary"
                        icon={<ShoppingCartOutlined />}
                        onClick={() => handleAddToCart(item, cartQuantities[item.id] || 1)}
                        block
                      >
                        Add to Cart
                      </Button>
                    </Space>
                  ]}
                  bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                >
                  <Card.Meta
                    title={
                      <div>
                        <Text strong ellipsis>{item.name}</Text>
                        <div style={{ marginTop: 8 }}>
                          <Space wrap>
                          {item.hasDiscount && item.retailPrice ? (
                            <>
                              <Text style={{ fontSize: 20, color: '#52c41a', fontWeight: 'bold' }}>
                                ${Number(item.unitPrice).toFixed(2)}
                              </Text>
                              <Text 
                                delete 
                                type="secondary" 
                                  style={{ fontSize: 14 }}
                              >
                                ${Number(item.retailPrice).toFixed(2)}
                              </Text>
                                <Tag color="green">
                                {item.discountPercent}% OFF
                              </Tag>
                            </>
                          ) : (
                            <Text style={{ fontSize: 20, color: '#1890ff', fontWeight: 'bold' }}>
                              ${Number(item.unitPrice).toFixed(2)}
                            </Text>
                          )}
                            {item.stockStatus === 'in_stock' && (
                              <Tag color="green">In Stock</Tag>
                            )}
                            {(item.stockStatus === 'special_order' || item.stockStatus === 'out_of_stock') && (
                              <Tag color="orange">Special Order</Tag>
                            )}
                          </Space>
                        </div>
                      </div>
                    }
                    description={
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Space direction="vertical" size="small" style={{ width: '100%', flex: 1 }}>
                          {item.description && (
                            <Tooltip 
                              title={item.description.length > 100 ? item.description : null}
                              placement="top"
                            >
                              <Paragraph 
                                style={{ 
                                  marginBottom: 8, 
                                  fontSize: '14px', 
                                  fontWeight: 500,
                                  color: 'rgba(0, 0, 0, 0.85)',
                                  minHeight: '40px'
                                }}
                                ellipsis={{ rows: 2, expandable: false }}
                              >
                                {item.description}
                              </Paragraph>
                            </Tooltip>
                          )}
                          <Space wrap>
                            {item.category && <Tag color="blue">{item.category}</Tag>}
                            {item.itemType && <Tag color="green">{item.itemType}</Tag>}
                          </Space>
                          {item.pricingTiers && item.pricingTiers.length > 0 && (
                            <QuantityBreakDisplay
                              pricingTiers={item.pricingTiers}
                              basePrice={item.retailPrice || Number(item.unitPrice)}
                              hasCustomerDiscount={item.hasDiscount}
                            />
                          )}
                          {item.attributes && Object.keys(item.attributes).length > 0 && (
                            <Collapse 
                              ghost 
                              size="small"
                              items={[{
                                key: 'attributes',
                                label: `Attributes (${Object.keys(item.attributes).length})`,
                                children: (
                                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    {Object.entries(item.attributes).map(([key, value]) => (
                                      <div key={key}>
                                        <Text strong>{key}:</Text> <Text>{value}</Text>
                                      </div>
                                    ))}
                                  </Space>
                                )
                              }]}
                            />
                          )}
                        </Space>
                      </div>
                    }
                  />
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card>

      {/* Special Order Section */}
      <Card 
        title={
          <Space>
            <StarOutlined style={{ color: '#faad14' }} />
            <span>Special Order Request</span>
          </Space>
        }
        style={{ marginTop: 24 }}
        extra={
          <Tag color="gold">Can't find what you need?</Tag>
        }
      >
        <Paragraph type="secondary" style={{ marginBottom: 24 }}>
          Looking for something specific that's not in our catalog? Submit a special order request and our team will provide you with a custom quote.
        </Paragraph>

        <Form
          form={specialOrderForm}
          layout="vertical"
          onFinish={handleSubmitSpecialOrder}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: 'Please select a category' }]}
              >
                <Select
                  placeholder="Select a category"
                  loading={loadingCategories}
                  showSearch
                  optionFilterProp="children"
                >
                  {specialOrderCategories.map(cat => (
                    <Option key={cat.id} value={cat.category}>
                      {cat.category}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="itemType"
                label="Item Type"
              >
                <Select placeholder="Select item type" allowClear>
                  <Option value="material">Material</Option>
                  <Option value="part">Part</Option>
                  <Option value="equipment">Equipment</Option>
                  <Option value="fixture">Fixture</Option>
                  <Option value="appliance">Appliance</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Item Description"
            rules={[{ required: true, message: 'Please describe the item you need' }]}
            extra="Please provide as much detail as possible including brand, model, size, color, or any specific requirements."
          >
            <Input.TextArea
              rows={4}
              placeholder="Example: I need a Kohler K-3817-0 Memoirs Stately Comfort Height toilet in white. Looking for a dual-flush model if available..."
            />
          </Form.Item>

          <Form.Item
            label="Reference Image (Optional)"
            extra="Upload a photo of the item you're looking for"
          >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Upload {...uploadProps}>
                <Button 
                  icon={uploadingImage ? <LoadingOutlined /> : <UploadOutlined />}
                  loading={uploadingImage}
                >
                  {uploadingImage ? 'Uploading...' : 'Upload Image'}
                </Button>
              </Upload>
              {specialOrderImageUrl && (
                <div style={{ marginTop: 8 }}>
                  <Image
                    src={specialOrderImageUrl}
                    alt="Special order reference"
                    width={200}
                    style={{ borderRadius: 8, border: '1px solid #d9d9d9' }}
                  />
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => setSpecialOrderImageUrl(null)}
                    style={{ marginLeft: 8 }}
                  >
                    Remove
                  </Button>
                </div>
              )}
            </Space>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              icon={<StarOutlined />}
              loading={submittingSpecialOrder}
              size="large"
              style={{ background: '#faad14', borderColor: '#faad14' }}
            >
              {submittingSpecialOrder ? 'Submitting...' : 'Submit Special Order Request'}
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Special Order Success Modal */}
      <Modal
        title="Special Order Submitted!"
        open={isSpecialOrderModalVisible}
        onCancel={handleCloseSpecialOrderSuccess}
        footer={null}
        width={500}
      >
        <Result
          status="success"
          icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
          title="Special Order Request Submitted!"
          subTitle={
            <Space direction="vertical" size="small" style={{ textAlign: 'center' }}>
              <Text>
                Estimate #{specialOrderResult?.estimate?.estimateNumber}
              </Text>
              <Text type="secondary">
                {specialOrderResult?.estimate?.title}
              </Text>
            </Space>
          }
          extra={
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Alert
                message="What happens next?"
                description={
                  specialOrderResult?.nextSteps || 
                  "Our team will review your request and provide you with a quote. You'll receive an email when your quote is ready."
                }
                type="info"
                showIcon
              />
              
              <Space style={{ width: '100%', justifyContent: 'center' }}>
                <Button
                  type="primary"
                  size="large"
                  icon={<FileTextOutlined />}
                  onClick={handleViewSpecialOrderEstimate}
                >
                  View Estimate
                </Button>
                <Button
                  size="large"
                  onClick={handleCloseSpecialOrderSuccess}
                >
                  Done
                </Button>
              </Space>
            </Space>
          }
        />
      </Modal>

      {/* Request Quote Modal */}
      <Modal
        title={quoteResult ? "Quote Request Submitted!" : "Request a Quote"}
        open={isQuoteModalVisible}
        onCancel={handleCloseSuccess}
        footer={null}
        width={600}
      >
        {quoteResult ? (
          // Success Result View
          <Result
            status="success"
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title="Estimate Created!"
            subTitle={
              <Space direction="vertical" size="small" style={{ textAlign: 'center' }}>
                <Text strong style={{ fontSize: 18 }}>
                  Estimate #{quoteResult.estimate?.estimateNumber}
                </Text>
                <Text style={{ fontSize: 24, color: '#1890ff', fontWeight: 'bold' }}>
                  ${Number(quoteResult.estimate?.total || 0).toFixed(2)}
                </Text>
              </Space>
            }
            extra={
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {quoteResult.emailSent && (
                  <Alert
                    message="Email Sent"
                    description="You will receive an email with your estimate details. You can review and approve it directly from the email."
                    type="info"
                    showIcon
                    icon={<MailOutlined />}
                  />
                )}
                
                <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text strong>What happens next?</Text>
                    <Text type="secondary">
                      1. Review your estimate details<br/>
                      2. Approve the estimate when ready<br/>
                      3. We'll schedule your service
                    </Text>
                  </Space>
                </Card>

                <Space style={{ width: '100%', justifyContent: 'center' }}>
                  <Button
                    type="primary"
                    size="large"
                    icon={<FileTextOutlined />}
                    onClick={handleViewEstimate}
                  >
                    View & Approve Now
                  </Button>
                  <Button
                    size="large"
                    onClick={handleCloseSuccess}
                  >
                    Done
                  </Button>
                </Space>
              </Space>
            }
          />
        ) : (
          // Quote Request Form
          <Form
            form={quoteForm}
            layout="vertical"
            onFinish={handleRequestQuote}
          >
            <Paragraph type="secondary">
              Fill out your contact information and we'll send you a detailed quote for the items in your cart.
            </Paragraph>

            <Form.Item
              name="name"
              label="Full Name"
              rules={[{ required: true, message: 'Please enter your name' }]}
            >
              <Input placeholder="John Doe" />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input placeholder="john@example.com" />
            </Form.Item>

            <Form.Item
              name="phone"
              label="Phone Number"
              rules={[{ required: true, message: 'Please enter your phone number' }]}
            >
              <Input placeholder="(555) 123-4567" />
            </Form.Item>

            <Form.Item
              name="address"
              label="Service Address (Optional)"
            >
              <Input.TextArea rows={2} placeholder="123 Main St, City, State ZIP" />
            </Form.Item>

            <Form.Item
              name="message"
              label="Additional Notes (Optional)"
            >
              <Input.TextArea rows={3} placeholder="Any specific requirements or questions..." />
            </Form.Item>

            {/* Cart Summary in Modal */}
            <Card size="small" style={{ marginBottom: 16, background: '#f5f5f5' }}>
              <Text strong>Quote Summary:</Text>
              <div style={{ marginTop: 8 }}>
                {cart.map((cartItem, index) => (
                  <div key={index} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text>{cartItem.quantity}x {cartItem.item.name}</Text>
                    <Text>
                      ${(Number(cartItem.item.unitPrice) * cartItem.quantity).toFixed(2)}
                    </Text>
                  </div>
                ))}
                <div style={{ borderTop: '1px solid #d9d9d9', marginTop: 8, paddingTop: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text strong>Total:</Text>
                    <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
                      ${cartTotal.toFixed(2)}
                    </Text>
                  </div>
                </div>
              </div>
            </Card>

            <Form.Item style={{ marginBottom: 0 }}>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={handleCloseSuccess} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  icon={<MailOutlined />}
                  loading={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Send Quote Request'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Help Section */}
      <Card style={{ marginTop: 24, background: '#f0f7ff', borderColor: '#91d5ff' }}>
        <Space direction="vertical" size="small">
          <Text strong>Need Help?</Text>
          <Paragraph style={{ marginBottom: 0 }}>
            <PhoneOutlined /> Call us at (555) 123-4567 or <MailOutlined /> email support@example.com
          </Paragraph>
        </Space>
      </Card>
    </div>
  );
};

export default ProductSearch;
